// RequireAuth — route guard for the post-login surface.
//
// Decision matrix (in order, top wins):
//
//   tenant.isPending             → render nothing (parent layout shows the
//                                  shell; the gated child is held)
//   tenant.unknownTenant         → redirect to /login (LoginPage renders the
//                                  unknown-tenant alert; AC8 forbids /auth/me
//                                  here, which is upheld because we never get
//                                  past this branch)
//   no session                   → redirect to /login?return_to=<encoded>
//   status === 'wrong_tenant'    → redirect to /login (LoginPage shows the
//                                  Wrong-Tenant alert from `status`)
//   session + tenant match       → render <Outlet/>
//
// Note: the wrong-tenant DETECTION happens in <TenantGate>. RequireAuth only
// REACTS to the cleared state (status === 'wrong_tenant' or session === null
// + status === 'wrong_tenant'). This split keeps the side effect at the root
// where it can run before any protected fetch.

import type { ReactElement } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "./store.js";
import { useTenantContext } from "./TenantGate.js";
import { isPopoutWindow, popoutShouldShowSessionEnded } from "./windowRole.js";
import { PopoutSessionEnded } from "./PopoutSessionEnded.js";

export function RequireAuth(): ReactElement | null {
  const tenantQuery = useTenantContext();
  const session = useAuthStore((s) => s.session);
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (tenantQuery.isPending) return null;

  // ANA-R1-09 (AC2/AC3) — a pop-out window must NEVER mount the login redirect
  // flow: signing in from a pop-out would create a second, parallel session.
  // While the main window is authenticated the pop-out stays authed via the
  // auth BroadcastChannel, so it never reaches these branches. If the main
  // session genuinely ends (no session / wrong-tenant / unknown-tenant), the
  // pop-out shows an inline "session ended" notice instead of navigating to
  // /login.
  if (isPopoutWindow()) {
    const sessionEnded = popoutShouldShowSessionEnded({
      unknownTenant: tenantQuery.unknownTenant,
      status,
      hasSession: session !== null,
      sessionTenantSlug: session?.tenant.tenantSlug ?? null,
      resolvedTenantSlug: tenantQuery.tenant?.tenantSlug ?? null,
    });
    return sessionEnded ? <PopoutSessionEnded /> : <Outlet />;
  }

  if (tenantQuery.unknownTenant) {
    return <Navigate to="/login" replace />;
  }

  if (status === "wrong_tenant") {
    // Wrong-tenant cleared state — bounce to /login; the Login page
    // surfaces the alert from status.
    return <Navigate to="/login" replace />;
  }

  if (!session) {
    const returnTo = encodeURIComponent(
      `${location.pathname}${location.search}`,
    );
    return <Navigate to={`/login?return_to=${returnTo}`} replace />;
  }

  // Defensive: tenant mismatch slipped through (race between TenantGate's
  // effect and this render). Treat as wrong-tenant. Compares slugs (see
  // TenantGate.tsx for the rationale on slug-vs-tenantId).
  if (
    tenantQuery.tenant &&
    session.tenant.tenantSlug !== tenantQuery.tenant.tenantSlug
  ) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
